import React, { useContext, useRef } from "react";

import { observer } from "mobx-react-lite";

import { ExpressionNode } from "@/components/expression/ExpressionNode";
import { useGetBracketsSvgPathsBySymbol } from "@/components/expression/utils/groups/useGetBracketsSvgPathsBySymbol";
import { SelectedContext } from "@/components/expression/utils/layout";
import * as Node from "@/components/expression/utils/layout";
import { Group as NodeGroup } from "@/components/expression/utils/NodeLayout";
import { NodeStyleProvider, useNodeStyle } from "@/components/expression/utils/NodeStyleProvider";
import { useNodeHover } from "@/components/expression/utils/useNodeHover";
import { twMerge } from "@/utils/twMerge";

import { bracketsFromSlice } from "../../inline-node/wrapping/buildRowTree";

import type { GroupSlice } from "@/components/expression/inline-node/wrapping/types";
import type { NodePlaceholders } from "@/components/suggestions/types";
import type { NodeModel } from "@/models/nodes/types";
import type { OpeningSymbol, ClosingSymbol } from "@corca/data";

type GroupParametersProps = {
  node: NodeModel;
  nodePlaceholders?: NodePlaceholders;
  parameters: NodeModel[];
  className?: string;
  isHighlighted?: boolean;
};

export const GroupParameters = observer(function GroupParameters({
  node,
  nodePlaceholders,
  parameters,
  className,
  isHighlighted,
}: GroupParametersProps) {
  const { doc } = node;
  const isContextSelected = useContext(SelectedContext);
  const isActive = doc.selection.isActive(node);

  return (
    <SelectedContext.Provider value={isActive || isContextSelected}>
      <div className={twMerge("flex items-baseline relative", className)}>
        {parameters.map((inline, i) => {
          const placeholderAppearance = nodePlaceholders?.children?.[i];
          const placeholderVisible = nodePlaceholders?.childrenVisible?.[i];

          let nodeEl: React.ReactNode = (
            <ExpressionNode node={inline} className="flex-none self-baseline" />
          );
          if (placeholderAppearance) {
            nodeEl = (
              <NodeStyleProvider placeholderAppearance={placeholderAppearance}>
                {nodeEl}
              </NodeStyleProvider>
            );
          }

          if (inline.childrenIds.length === 0 && placeholderVisible === "none") {
            nodeEl = null;
          }

          return (
            <React.Fragment key={inline.id}>
              {nodeEl}
              {i !== parameters.length - 1 && (
                <Node.Value className="self-center relative z-10">,</Node.Value>
              )}
            </React.Fragment>
          );
        })}
        {isHighlighted && (
          <div
            className={twMerge(
              "absolute left-0 -bottom-[3px] w-full h-[1px] border-b border-edit-border z-10 print:hidden",
            )}
          />
        )}
      </div>
    </SelectedContext.Provider>
  );
});

export const GroupHighlightedContext = React.createContext<boolean>(false);

export const useGroupHighligtedContext = () => useContext(GroupHighlightedContext);

type SvgGroupProps = {
  node: NodeModel;
  nodePlaceholders?: NodePlaceholders;
  parameters: NodeModel[];
  openingSymbol?: OpeningSymbol;
  closingSymbol?: ClosingSymbol;
  className?: string;
  bracketsClassName?: string;
  bgClassName?: string;
  disabled?: boolean;
  isHighlighted?: boolean;
  compensateBracketWidthLeft?: number;
  compensateBracketWidthRight?: number;
  hideParentheses?: boolean;
  hideMissingParentheses?: boolean;
  slice?: GroupSlice;
  children?: React.ReactNode;
};

export const SvgGroup = observer(function SvgGroup({
  node,
  nodePlaceholders,
  parameters,
  openingSymbol,
  closingSymbol,
  className,
  bracketsClassName,
  bgClassName,
  disabled,
  compensateBracketWidthLeft,
  compensateBracketWidthRight,
  hideParentheses,
  hideMissingParentheses,
  isHighlighted,
  slice = "whole",
  children,
}: SvgGroupProps) {
  const { doc } = node;

  const containerRef = useRef<HTMLDivElement>(null);
  const isContextSelected = useContext(SelectedContext);
  const { isHovered, handlerProps } = useNodeHover(node);

  const { viewBox, paths } = useGetBracketsSvgPathsBySymbol(containerRef, node, {
    openingSymbol,
    closingSymbol,
  });

  const isActive = doc.selection.isActive(node);
  const isSelected = isContextSelected || isActive || doc.selection.isSelected(node);
  const isFocused = doc.cursor.nodesAreFocused(parameters);

  const highlightBrackets = isFocused || isHovered || isActive;

  const { showLeft: hasLeftSide, showRight: hasRightSide } = bracketsFromSlice(slice);
  const showLeft = !hideParentheses && hasLeftSide;
  const showRight = !hideParentheses && hasRightSide;
  const showBody = !hideParentheses && (showLeft || showRight);

  return (
    <div
      ref={containerRef}
      className={twMerge("relative flex flex-row items-baseline", className)}
      style={{
        marginLeft:
          showLeft && typeof compensateBracketWidthLeft === "number"
            ? `-${compensateBracketWidthLeft + paths.left.bracketWidth}px`
            : undefined,
        marginRight:
          showRight && typeof compensateBracketWidthRight === "number"
            ? `-${compensateBracketWidthRight + paths.right.bracketWidth}px`
            : undefined,
      }}
      {...handlerProps}
    >
      <NodeGroup
        className={twMerge(
          hideParentheses && "px-0 py-0",
          !showLeft && "pl-0",
          !showRight && "pr-0",
        )}
        style={{
          paddingLeft: showLeft && paths?.left.padding ? `${paths.left.padding}px` : undefined,
          paddingRight: showRight && paths?.right.padding ? `${paths.right.padding}px` : undefined,
        }}
      >
        {(showLeft || showRight) && paths && (
          <svg
            className={twMerge("absolute left-0 top-0 h-full w-full")}
            fill="none"
            preserveAspectRatio="none"
            width={viewBox.width}
            height={viewBox.height}
          >
            {showBody && (
              <path
                className={twMerge(
                  "fill-transparent print:fill-transparent",
                  bgClassName,
                  isSelected && "fill-selected-bg",
                )}
                d={paths.body}
              />
            )}
            {showLeft && (
              <path
                className={twMerge(
                  "stroke-document-text print:stroke-document-text",
                  bracketsClassName,
                  (disabled || !openingSymbol) && "opacity-20",
                  highlightBrackets && "stroke-node-active",
                  hideMissingParentheses && !openingSymbol && "hidden",
                )}
                d={paths.left.path}
              />
            )}
            {showRight && (
              <path
                className={twMerge(
                  "stroke-document-text print:stroke-document-text",
                  bracketsClassName,
                  (disabled || !closingSymbol) && "opacity-20",
                  highlightBrackets && "stroke-node-active",
                  hideMissingParentheses && !closingSymbol && "hidden",
                )}
                d={paths.right.path}
              />
            )}
          </svg>
        )}
        {showLeft && openingSymbol && (
          <span aria-hidden className="hidden">
            {openingSymbol}
          </span>
        )}
        {children ?? (
          <GroupParameters
            node={node}
            nodePlaceholders={nodePlaceholders}
            parameters={parameters}
            isHighlighted={isHighlighted}
          />
        )}
        {showRight && closingSymbol && (
          <span aria-hidden className="hidden">
            {closingSymbol}
          </span>
        )}
      </NodeGroup>
    </div>
  );
});

type TextBracketProps = {
  symbol: string;
  className?: string;
};

function TextBracket({ symbol, className }: TextBracketProps) {
  const { text } = useNodeStyle();

  return (
    <Node.Value
      className={twMerge(className, text === "unit" && "text-gray-300 dark:text-gray-700")}
    >
      {symbol}
    </Node.Value>
  );
}

export const Group = observer(function Group(props: SvgGroupProps) {
  const { view } = useNodeStyle();
  const {
    openingSymbol,
    closingSymbol,
    parameters,
    hideParentheses,
    className,
    bracketsClassName,
    slice = "whole",
    children,
  } = props;

  if (view === "text") {
    const { showLeft: hasLeftSide, showRight: hasRightSide } = bracketsFromSlice(slice);
    const showLeft = !hideParentheses && hasLeftSide;
    const showRight = !hideParentheses && hasRightSide;

    return (
      <NodeGroup className={className}>
        {showLeft && openingSymbol && (
          <TextBracket symbol={openingSymbol} className={bracketsClassName} />
        )}
        {children ?? (
          <GroupParameters
            node={props.node}
            nodePlaceholders={props.nodePlaceholders}
            parameters={parameters}
          />
        )}
        {showRight && closingSymbol && (
          <TextBracket symbol={closingSymbol} className={bracketsClassName} />
        )}
      </NodeGroup>
    );
  }

  return <SvgGroup {...props} />;
});
